/**
 * Message Input Component for dev-tools
 * Part of Phase 3 refactoring - Component Decomposition (UI Components)
 */
import React, { useMemo, useState } from "react";

import { Box, Button, Chip, Stack, TextField, Typography } from "@mui/material";

import { FieldSchema, FormState } from "../../types";
import { InputFactory } from "../input-factory";

interface MessageInputProps {
  field: FieldSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  touched?: boolean;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  field,
  value,
  onChange,
  error,
  touched,
}) => {
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const hasNestedFields = field.messageFields && field.messageFields.length > 0;

  // Initialize nested form state
  const nestedFormState = useMemo((): FormState => {
    const currentValue =
      typeof value === "object" && value !== null
        ? (value as Record<string, unknown>)
        : {};

    return {
      data: currentValue,
      errors: {},
      touched: {},
      isValid: true,
      isSubmitting: false,
    };
  }, [value]);

  const handleNestedFieldChange = (fieldName: string, fieldValue: unknown) => {
    const currentValue =
      typeof value === "object" && value !== null
        ? (value as Record<string, unknown>)
        : {};
    const newValue = {
      ...currentValue,
      [fieldName]: fieldValue,
    };
    onChange(newValue);
  };

  const handleJsonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const jsonValue = JSON.parse(e.target.value);
      onChange(jsonValue);
    } catch {
      // If not valid JSON, store as string
      onChange(e.target.value);
    }
  };

  const displayValue =
    typeof value === "object" && value !== null
      ? JSON.stringify(value, null, 2)
      : String(value || "{}");

  const renderJsonEditor = () => (
    <TextField
      multiline
      rows={3}
      fullWidth
      name={field.name}
      placeholder="Enter JSON object or leave empty for default"
      value={displayValue}
      onChange={handleJsonChange}
      error={!!error && touched}
      helperText={error && touched ? error : "Enter a valid JSON object"}
      size="small"
      variant="outlined"
      sx={{
        "& .MuiInputBase-input": {
          fontFamily: "Monaco, Consolas, 'Courier New', monospace",
          fontSize: "0.875rem",
          textAlign: "left",
          lineHeight: 1.5,
        },
        "& .MuiInputBase-root": {
          backgroundColor: "background.paper",
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
        },
        "& .MuiFormHelperText-root": {
          margin: "4px 0 0 0",
        },
      }}
    />
  );

  const renderNestedFields = () => {
    if (!hasNestedFields || !field.messageFields) return null;

    return (
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          p: 1.5,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {field.messageFields.map((nestedField) => (
            <Box key={nestedField.name}>
              <InputFactory
                field={nestedField}
                value={nestedFormState.data[nestedField.name]}
                onChange={handleNestedFieldChange}
                error={nestedFormState.errors[nestedField.name]}
                touched={nestedFormState.touched[nestedField.name]}
              />
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: "600",
            color: "text.primary",
            fontSize: "0.9rem",
          }}
        >
          {field.name} (Message)
          {!field.isOptional && " *"}
        </Typography>
        {hasNestedFields && field.messageFields && (
          <Chip
            label={`${field.messageFields.length} fields`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              fontSize: "0.75rem",
              height: 20,
            }}
          />
        )}
      </Box>

      {field.description && (
        <Typography
          variant="caption"
          sx={{
            mb: 1.5,
            display: "block",
            color: "text.secondary",
            fontSize: "0.8rem",
            lineHeight: 1.4,
          }}
        >
          {field.description}
        </Typography>
      )}

      {hasNestedFields ? (
        <Box>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              size="small"
              variant={!showJsonEditor ? "contained" : "outlined"}
              onClick={() => setShowJsonEditor(false)}
              sx={{
                minWidth: 120,
                textTransform: "none",
                fontWeight: showJsonEditor ? "normal" : "medium",
              }}
            >
              Structured Form
            </Button>
            <Button
              size="small"
              variant={showJsonEditor ? "contained" : "outlined"}
              onClick={() => setShowJsonEditor(true)}
              sx={{
                minWidth: 120,
                textTransform: "none",
                fontWeight: !showJsonEditor ? "normal" : "medium",
              }}
            >
              JSON Editor
            </Button>
          </Stack>

          <Box>
            {showJsonEditor ? renderJsonEditor() : renderNestedFields()}
          </Box>
        </Box>
      ) : (
        renderJsonEditor()
      )}

      {error && touched && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 1, display: "block" }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};
